import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Output,
  PLATFORM_ID,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { CartService } from '../../shared/services/cart.service';
import { AuthService } from '../../shared/services/auth.service';
import { BuyNowButtonComponent } from '../buy-now-button/buy-now-button.component';
import { BuyNowPopupComponent } from '../buy-now-popup/buy-now-popup.component';
import { LoaderService } from '../../shared/services/loader.service';
import { ScrapCertificateService } from '../../shared/services/scrap-certificate.service';
import { ScrapCertificateModalComponent } from '../../shared/components/scrap-certificate-modal/scrap-certificate-modal.component';
import { BehaviourSubjectService } from '../../shared/services/behaviour-subject.service';
import { ProductNamePipe } from '../../shared/pipes/product-name.pipe';

@Component({
  selector: 'app-recently-viewed-parts',
  standalone: true,
  imports: [
    CommonModule,
    BuyNowButtonComponent,
    BuyNowPopupComponent,
    ScrapCertificateModalComponent,
    ProductNamePipe,
  ],
  templateUrl: './recently-viewed-parts.component.html',
  styleUrl: './recently-viewed-parts.component.scss',
})
export class RecentlyViewedPartsComponent implements OnInit, OnDestroy {
  @Output() buyNowClicked = new EventEmitter<string>();
  @Output() addToCartClicked = new EventEmitter<string>();

  currentSlide = 0;
  visibleSlides = 1;
  slideWidth = 310;
  gap = 20;
  isBrowser: boolean;

  products: any[] = [];

  isMobile: boolean = false;
  isLoggedIn: boolean = false;
  userData: any;
  private sub: Subscription = new Subscription();
  productslist: any;

  isBuyNowPopupOpen = false;
  showScrapCertificateModal = false;
  selectedProductForBuyNow: any = null;
  pendingBuyNowProduct: any = null;
  recentlyViewedProducts: any;
  hoveredProduct: any = null;
  itemsAddedToCart: Set<string> = new Set(); // Track cart state per product
  imageScrollInterval: any = null; // Store interval timer for auto-scrolling images
  private dataLoaded = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private apiService: ApiService,
    private router: Router,
    private cartService: CartService,
    private authService: AuthService,
    private loaderService: LoaderService,
    private scrapCertificateService: ScrapCertificateService,
    private behaviourSubject: BehaviourSubjectService,
    private cdr: ChangeDetectorRef,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.productslist = JSON.parse(
        localStorage.getItem('visitedProductIds') || '[]',
      );
      this.updateVisibleSlides();

      // Subscribe to authentication state changes
      this.sub.add(
        this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
          this.isLoggedIn = isLoggedIn;
          if (isLoggedIn && this.pendingBuyNowProduct) {
            setTimeout(() => {
              this.selectedProductForBuyNow = this.pendingBuyNowProduct;
              this.isBuyNowPopupOpen = true;
              this.pendingBuyNowProduct = null;
              this.cdr.detectChanges();
            }, 500);
          }
        }),
      );

      // Subscribe to user data changes
      this.sub.add(
        this.authService.userData$.subscribe((userData) => {
          this.userData = userData;
          // this.checkAndLoadData();
        }),
      );

      // Initial check for existing auth state
      this.checkAndLoadData();
    }

    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      )
    ) {
      this.isMobile = true;
    } else {
      this.isMobile = false;
    }

    // Sync scrap certificate modal state with shared service
    this.sub.add(
      this.scrapCertificateService.showScrapCertificateModal$.subscribe(
        (show) => {
          this.showScrapCertificateModal = show;
          this.cdr.detectChanges();
        },
      ),
    );
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isBrowser) {
      this.updateVisibleSlides();
    }
  }

  updateVisibleSlides(): void {
    if (!this.isBrowser) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < 640) {
      // sm breakpoint - mobile
      this.visibleSlides = 1;
      this.slideWidth = 310;
      this.gap = 8;
    } else if (windowWidth < 768) {
      // md breakpoint - small tablet
      this.visibleSlides = 2;
      this.slideWidth = 310;
      this.gap = 12;
    } else if (windowWidth < 1024) {
      // lg breakpoint - tablet (4 cards at 180px each)
      this.visibleSlides = 4;
      this.slideWidth = 190; // 180px card width + 10px gap
      this.gap = 10;
    } else {
      // desktop and larger (4 cards at 300px each)
      this.visibleSlides = 4;
      this.slideWidth = 320; // 300px card width + 20px gap
      this.gap = 20;
    }
  }

  checkAndLoadData(): void {
    if (this.dataLoaded) return;

    if (this.userData && this.isLoggedIn) {
      this.dataLoaded = true;

      // Sequential API pattern by design:
      // 1. If localStorage has visited product IDs, send them via ADD_DATA
      // 2. ADD_DATA clears localStorage and triggers GET_LIST to fetch full details
      // 3. If no local IDs, skip directly to GET_LIST
      if (this.productslist && this.productslist.length > 0) {
        this.getAddDataProductList(); // Will call getDataProductList() on success
      } else {
        this.getDataProductList();
      }
    }
  }

  next(): void {
    if (this.currentSlide < this.products.length - this.visibleSlides) {
      this.currentSlide++;
    }
  }

  prev(): void {
    if (this.currentSlide > 0) {
      this.currentSlide--;
    }
  }

  onBuyNowClicked(productId: any): void {
    this.onBuyNow(productId);
  }

  onAddToCartClicked(event: Event, productId: any): void {
    event.stopPropagation(); // Prevent event bubbling to parent elements
    // Mark as touched so validation messages can show after click
    if (productId) {
      productId.quantityTouched = true;
      this.cdr.markForCheck();
    }

    // Validate selected quantity for all products against fix_quantity
    if (!this.isQuantityAvailable(productId)) {
      return;
    }

    // Use the shared service to handle scrap part detection
    this.cartService.handleAddToCart(
      productId, // Pass the product data
      'Store Pickup',
      this.getQuantityForPopup(productId),
      productId.selectedUnit === 'kg'
        ? 'Kg'
        : productId.selectedUnit === 'liter'
          ? 'Liter'
          : 'Ton',
      () => {
        // Callback for non-scrap parts
        this.addToCartForNonScrapParts(productId);
      },
      () => {
        // Callback for login required
        this.openLoginPopup();
      },
    );
  }
  // onAddToCartClicked(productId: any): void {
  //   // Check if quantity validation fails
  //   if (this.shouldDisableButtons(productId)) {
  //     console.log('Add to Cart: Button is disabled due to quantity validation');
  //     return;
  //   }

  //   // Use the shared service to handle scrap part detection
  //   this.cartService.handleAddToCart(
  //     productId, // Pass the product data
  //     'Store Pickup',
  //     this.getQuantityForPopup(productId),
  //     productId.selectedUnit === 'kg' ? 'Kg' : 'Ton',
  //     () => {
  //       // Callback for non-scrap parts
  //       this.addToCartForNonScrapParts(productId);
  //     },
  //     () => {
  //       // Callback for login required
  //       this.openLoginPopup();
  //     }
  //   );
  // }

  private addToCartForNonScrapParts(productId: any): void {
    if (!this.isLoggedIn) {
      // Find the product data from the products array
      const productData = this.products.find(
        (product: any) => product.inventory_id === productId?.inventory_id,
      );
      if (productData) {
        // Get quantity and unit for scrap parts
        const quantity = this.getQuantityForPopup(productData);
        const unit =
          productData.selectedUnit === 'kg'
            ? 'Kg'
            : productData.selectedUnit === 'liter'
              ? 'Liter'
              : 'Ton';

        // Add to local storage for non-logged in users with complete product data
        this.cartService.addToLocalStorage(
          productData,
          'Store Pickup',
          quantity,
          unit,
        );
        this.itemsAddedToCart.add(productId?.inventory_id); // Track this specific product
        this.addToCartClicked.emit(productId?.inventory_id);
        this.cdr.detectChanges(); // Trigger change detection to show "Go to Cart" button immediately
      }
      return;
    }
    localStorage.removeItem('addtocartpart_type');
    localStorage.setItem('addtocartpart_type', productId?.part_type);

    // Get quantity and unit for scrap parts
    const quantity = this.getQuantityForPopup(productId);
    const unit =
      productId.selectedUnit === 'kg'
        ? 'Kg'
        : productId.selectedUnit === 'liter'
          ? 'Liter'
          : 'Ton';

    this.sub.add(
      this.cartService
        .addToCart(productId?.inventory_id, 'Store Pickup', quantity, unit)
        .subscribe({
          next: () => {
            this.itemsAddedToCart.add(productId?.inventory_id); // Track this specific product
            this.addToCartClicked.emit(productId?.inventory_id);
            this.cdr.detectChanges(); // Trigger change detection to show "Go to Cart" button immediately
            // Don't auto-navigate, just show the "Go to Cart" button
          },
          error: (err) => {
            console.error('Error adding to cart:', err);
          },
          complete: () => {
            // No loader needed
          },
        }),
    );
  }

  goToCart() {
    this.cartService.navigateToCart();
  }

  openLoginPopup() {
    // Use the shared login modal service
    this.behaviourSubject.openLoginModal('other');
  }

  getAddDataProductList() {
    this.loaderService.show();
    try {
      let data = {
        action_type: 'ADD_DATA',
        part_number_list:
          this.productslist && this.productslist.length > 0
            ? this.productslist
            : [],
      };

      this.sub.add(
        this.apiService.getRecentlyViewedProductlistData(data).subscribe({
          next: (value) => {
            this.loaderService.hide();
            if (value.success) {
              localStorage.removeItem('visitedProductIds');
              this.getDataProductList();
            } else {
              console.warn('Add data API returned success: false');
              this.products = [];
            }
          },
          error: (err) => {
            this.loaderService.hide();
            console.error('Error loading products:', err);

            // Handle specific error codes
            if (err.status === 412) {
              console.warn(
                'Precondition failed - server may be temporarily unavailable',
              );
            } else if (err.status === 401) {
              console.warn('Unauthorized - authentication required');
            } else if (err.status === 403) {
              console.warn('Forbidden - access denied');
            }

            this.products = [];
          },
        }),
      );
    } catch (error) {
      this.loaderService.hide();
      console.error('Error in getAddDataProductList:', error);
      this.products = [];
    }
  }
  getDataProductList() {
    this.loaderService.show();
    try {
      let data = {
        action_type: 'GET_LIST',
        limit: 12,
        offset: 0,
      };

      this.sub.add(
        this.apiService.getRecentlyViewedProductlistData(data).subscribe({
          next: (value) => {
            this.loaderService.hide();
            if (value.success) {
              // Handle different API response structures
              if (Array.isArray(value?.data)) {
                // If data is an array, use it directly
                this.recentlyViewedProducts = value.data.map(
                  (item: any) => item.part_number,
                );
                this.products = value.data;
              } else if (value?.data?.part_list) {
                // If data has part_list property
                this.recentlyViewedProducts = Array.isArray(
                  value.data.part_list,
                )
                  ? value.data.part_list.map((item: any) => item.part_number)
                  : [];
                this.products = value.data.part_list;
              } else {
                this.recentlyViewedProducts = [];
                this.products = [];
              }

              // Initialize product properties for hover functionality
              this.products.forEach((product) => {
                // Ensure part_image_path is always an array for hover functionality
                if (Array.isArray(product.part_image_path)) {
                  product.images = product.part_image_path;
                } else if (product.part_image_path) {
                  product.images = [product.part_image_path];
                  product.part_image_path = [product.part_image_path]; // Ensure it's an array
                } else {
                  product.images = [];
                  product.part_image_path = []; // Ensure it's an array
                }
                product.currentImageIndex = 0;
                // Initialize quantity and unit properties
                // Check if product is oil category with default_uom as 'litr' or 'liter'
                if (this.isOilProductWithLiter(product)) {
                  product.selectedUnit = 'liter'; // Set to liter for oil products
                } else {
                  product.selectedUnit = 'ton'; // Default unit is ton
                }
                // For scrap parts, require explicit selection (no default)
                if (product.part_type === 'Scrap Part') {
                  product.quantity = null;
                  product.customKgQuantity = null;
                  product.customTonQuantity = null;
                  if (this.isOilProductWithLiter(product)) {
                    product.customLiterQuantity = null; // For custom liter input
                  }
                } else {
                  product.quantity = 1; // Default for non-scrap
                  product.customKgQuantity = null;
                }
                product.quantityTouched = false; // Track user interaction with quantity
              });
            } else {
              console.warn('Get data API returned success: false');
              this.products = [];
            }
          },
          error: (err) => {
            this.loaderService.hide();
            console.error('Error loading products:', err);

            // Handle specific error codes
            if (err.status === 412) {
              console.warn(
                'Precondition failed - server may be temporarily unavailable',
              );
            } else if (err.status === 401) {
              console.warn('Unauthorized - authentication required');
            } else if (err.status === 403) {
              console.warn('Forbidden - access denied');
            }

            this.products = [];
          },
        }),
      );
    } catch (error) {
      this.loaderService.hide();
      console.error('Error in getDataProductList:', error);
      this.products = [];
    }
  }

  getProductList() {
    this.loaderService.show();
    try {
      let data = {
        limit: 12,
        offset: 0,
        // "get_plp_data": true,
        part_number_list:
          this.recentlyViewedProducts && this.recentlyViewedProducts.length > 0
            ? this.recentlyViewedProducts
            : [],
      };

      this.sub.add(
        this.apiService.getProductlistData(data).subscribe({
          next: (value) => {
            this.loaderService.hide();
            if (value.success) {
              this.products = value.data.part_list;

              this.products.forEach((product) => {
                if (Array.isArray(product.part_image_path)) {
                  product.images = product.part_image_path;
                } else if (product.part_image_path) {
                  product.images = [product.part_image_path];
                } else {
                  product.images = [];
                }
                product.currentImageIndex = 0;
                // Initialize quantity and unit properties
                // Check if product is oil category with default_uom as 'litr' or 'liter'
                if (this.isOilProductWithLiter(product)) {
                  product.selectedUnit = 'liter'; // Set to liter for oil products
                } else {
                  product.selectedUnit = 'ton'; // Default unit is ton
                }
                // For scrap parts, require explicit selection (no default)
                if (product.part_type === 'Scrap Part') {
                  product.quantity = null;
                  product.customKgQuantity = null;
                  product.customTonQuantity = null;
                  if (this.isOilProductWithLiter(product)) {
                    product.customLiterQuantity = null; // For custom liter input
                  }
                } else {
                  product.quantity = 1; // Default for non-scrap
                  product.customKgQuantity = null;
                }
                product.quantityTouched = false; // Track user interaction with quantity
              });
            } else {
              console.warn('Product list API returned success: false');
              this.products = [];
            }
          },
          error: (err) => {
            this.loaderService.hide();
            console.error('Error loading products:', err);

            // Handle specific error codes
            if (err.status === 412) {
              console.warn(
                'Precondition failed - server may be temporarily unavailable',
              );
            } else if (err.status === 401) {
              console.warn('Unauthorized - authentication required');
            } else if (err.status === 403) {
              console.warn('Forbidden - access denied');
            }

            this.products = [];
          },
        }),
      );
    } catch (error) {
      this.loaderService.hide();
      console.error('Error in getProductList:', error);
      this.products = [];
    }
  }

  gotoProductDetails(id: any) {
    localStorage.removeItem('product_categoryType');
    localStorage.setItem('product_categoryType', JSON.stringify(id?.part_type));
    this.router.navigate(['/product-detail/' + id?.part_number], {
      queryParams: { preserveScroll: 'true' },
    });
  }

  onBuyNow(product: any) {
    // For scrap parts, require explicit valid quantity selection before proceeding
    if (product?.part_type === 'Scrap Part') {
      product.quantityTouched = true;
      this.cdr.markForCheck();
      if (this.shouldDisableButtons(product)) {
        return;
      }
    }

    if (!this.isLoggedIn && !this.authService.isLoggedIn) {
      this.pendingBuyNowProduct = product;
      this.openLoginPopup();
      return;
    }

    if (this.shouldDisableButtons(product)) {
      product.quantityTouched = true;
      this.cdr.markForCheck();
      return;
    }

    // Check if this is a scrap part
    if (this.scrapCertificateService.isScrapPart(product)) {
      // For scrap parts, check certificate match before proceeding to buy now
      this.scrapCertificateService.handleAddToCart(
        product,
        () => {
          this.selectedProductForBuyNow = product;
          this.isBuyNowPopupOpen = true;
          this.cdr.detectChanges();
        },
        () => {
          this.pendingBuyNowProduct = product;
          this.openLoginPopup();
        },
      );
      return;
    }

    this.selectedProductForBuyNow = product;
    this.isBuyNowPopupOpen = true;
    this.cdr.detectChanges();
  }

  onPaymentSuccess(event: any) {
    this.closeBuyNowPopup();
    // Navigate to order confirmation page with order details
    if (event && event.data) {
      this.router.navigate(['/order-confirmation'], {
        state: { orderDetail: event.data },
      });
    } else {
      // Fallback navigation if no order details
      this.router.navigate(['/order-confirmation']);
    }
  }

  closeBuyNowPopup() {
    this.isBuyNowPopupOpen = false;
    this.selectedProductForBuyNow = null;
  }

  /**
   * Handles unit change (ton/kg/liter) for a product
   */
  onUnitChange(product: any, event: any) {
    const selectedUnit = event.target.value;
    product.selectedUnit = selectedUnit;
    product.quantityTouched = true;

    if (selectedUnit === 'kg') {
      // When switching to kg, reset custom quantity if not set
      if (!product.customKgQuantity) {
        product.customKgQuantity = '';
      }
      // Clear ton and liter-related properties
      product.quantity = '';
      product.customTonQuantity = '';
      product.customLiterQuantity = '';
    } else if (selectedUnit === 'liter') {
      // When switching to liter, reset custom quantity if not set
      if (!product.customLiterQuantity) {
        product.customLiterQuantity = '';
      }
      // Clear ton and kg-related properties
      product.quantity = '';
      product.customTonQuantity = '';
      product.customKgQuantity = '';
    } else {
      // When switching to ton, reset to default quantity
      product.quantity = '';
      product.customTonQuantity = '';
      product.customKgQuantity = '';
      product.customLiterQuantity = '';
    }
  }

  /**
   * Handles select dropdown changes for ton quantity
   */
  onTonQuantitySelect(product: any, event: any) {
    const value = event.target.value;
    product.quantityTouched = true;

    if (value === 'custom') {
      product.quantity = 'custom';
      product.customTonQuantity = '';
      // Focus the input field after Angular updates
      setTimeout(() => {
        const customInput = document.querySelector(
          `#customInput-${product.part_number}`,
        ) as HTMLInputElement;
        if (customInput) {
          customInput.focus();
        }
      }, 0);
    } else if (value) {
      product.quantity = parseFloat(value);
      product.customTonQuantity = '';
    } else {
      product.quantity = '';
      product.customTonQuantity = '';
    }
  }

  /**
   * Handles input changes for custom ton quantity
   */
  onCustomTonQuantityInput(product: any, event: any) {
    const value = event.target.value;
    product.customTonQuantity = value;
    product.quantityTouched = true;
  }

  /**
   * Handles blur event for custom ton quantity input
   */
  onCustomTonQuantityBlur(product: any, event: any) {
    const value = event.target.value;
    if (!value || parseFloat(value) <= 0) {
      // If invalid or empty, reset to select mode
      product.quantity = '';
      product.customTonQuantity = '';
    } else {
      product.customTonQuantity = parseFloat(value);
    }
    product.quantityTouched = true;
  }

  /**
   * Handles quantity change for ton units (legacy method for compatibility)
   */
  onQuantityChange(product: any, event: any) {
    const selectedQuantity = event.target.value;
    product.quantity = parseInt(selectedQuantity);
    product.quantityTouched = true;
  }

  /**
   * Handles custom kg quantity input
   */
  onKgQuantityChange(product: any, event: any) {
    const value = event.target.value;
    product.customKgQuantity = value;
    product.quantityTouched = true;
  }

  /**
   * Handles quantity change for liter units
   */
  onLiterQuantityChange(product: any, event: any) {
    const value = event.target.value;
    // Allow only numbers and decimal points
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      product.customLiterQuantity = value;
    }
    product.quantityTouched = true;
  }

  /**
   * Gets the current quantity display for a product
   */
  getCurrentQuantity(product: any): string {
    if (product.selectedUnit === 'kg') {
      return product.customKgQuantity || '';
    } else if (product.selectedUnit === 'liter') {
      return product.customLiterQuantity || '';
    }
    return product.quantity?.toString() || '1';
  }

  /**
   * Gets the quantity value for the buy now popup
   * Handles kg, ton, and liter units properly
   */
  getQuantityForPopup(product: any): number {
    if (!product) return 1;

    if (product.selectedUnit === 'kg') {
      // For kg units, return custom kg quantity or default to 1
      if (!product.customKgQuantity || product.customKgQuantity === '') {
        return 1;
      }
      const kg = parseFloat(product.customKgQuantity);
      return isNaN(kg) || kg <= 0 ? 1 : kg;
    } else if (product.selectedUnit === 'liter') {
      // For liter units, return custom liter quantity or default to 1
      if (!product.customLiterQuantity || product.customLiterQuantity === '') {
        return 1;
      }
      const liter = parseFloat(product.customLiterQuantity);
      return isNaN(liter) || liter <= 0 ? 1 : liter;
    } else {
      // For ton units, return custom when selected, else predefined quantity
      if (product.quantity === 'custom') {
        if (!product.customTonQuantity || product.customTonQuantity === '') {
          return 1;
        }
        const customTon = parseFloat(product.customTonQuantity);
        return isNaN(customTon) || customTon <= 0 ? 1 : customTon;
      }
      const qty =
        typeof product.quantity === 'number'
          ? product.quantity
          : parseFloat(product.quantity);
      return isNaN(qty) || qty <= 0 ? 1 : qty;
    }
  }

  /**
   * Gets the selected quantity for a product based on the current unit
   * Returns quantity in kg for consistent comparison
   */
  getSelectedQuantity(product: any): number {
    if (!product) return 0;
    if (product.part_type === 'Scrap Part') {
      if (product.selectedUnit === 'kg') {
        // For kg units, return custom kg quantity or 0 if not set
        if (!product.customKgQuantity || product.customKgQuantity === '') {
          return 0;
        }
        const qty = parseFloat(product.customKgQuantity);
        return isNaN(qty) ? 0 : qty;
      } else if (product.selectedUnit === 'liter') {
        // For liter units, return custom liter quantity or 0 if not set
        if (
          !product.customLiterQuantity ||
          product.customLiterQuantity === ''
        ) {
          return 0;
        }
        const qty = parseFloat(product.customLiterQuantity);
        return isNaN(qty) ? 0 : qty;
      } else {
        // For ton units, convert to kg (1 ton = 1000 kg)
        let tonQuantity = 0;
        if (product.quantity === 'custom') {
          if (!product.customTonQuantity || product.customTonQuantity === '') {
            tonQuantity = 0;
          } else {
            const qty = parseFloat(product.customTonQuantity);
            tonQuantity = isNaN(qty) ? 0 : qty;
          }
        } else if (
          product.quantity === '' ||
          product.quantity === null ||
          product.quantity === undefined
        ) {
          tonQuantity = 0;
        } else {
          tonQuantity = product.quantity || 0;
        }
        return tonQuantity * 1000;
      }
    } else {
      // For Used Parts, return quantity or default to 1
      return product.quantity || 1;
    }
  }

  /**
   * Gets the available quantity for a product in kg
   */
  getAvailableQuantity(product: any): number {
    if (!product) return 0;

    return (
      product.max_booking_qty ||
      product.max_booking_quantity ||
      product.max_quantity ||
      product.available_quantity ||
      product.fix_quantity ||
      0
    );
  }

  /**
   * Validates if the selected quantity is available
   * Compares quantities in kg for consistency
   */
  isQuantityAvailable(product: any): boolean {
    const selectedQuantityInKg = this.getSelectedQuantity(product);
    const availableQuantityInKg = this.getAvailableQuantity(product);
    // Use product.min_quantity or min_qty from API (default to 0 if not provided)
    const minQuantityInKg = product.min_quantity || product.min_qty || 0;

    // For used parts, if fix_quantity is 0 or not set, allow adding to cart
    // (similar to shouldDisableButtons logic)
    if (product.part_type !== 'Scrap Part') {
      // For used parts, if fix_quantity is 0 or not set, allow adding to cart
      // Only validate against fix_quantity if it's explicitly set and greater than 0
      if (availableQuantityInKg === 0 || !product.fix_quantity) {
        // Allow if minimum quantity requirement is met
        return (
          selectedQuantityInKg >= minQuantityInKg && selectedQuantityInKg > 0
        );
      }
    }

    return (
      selectedQuantityInKg >= minQuantityInKg &&
      selectedQuantityInKg <= availableQuantityInKg &&
      selectedQuantityInKg > 0
    );
  }

  /**
   * Checks if product is an oil/fluid product with liter (ltr) unit of measurement
   * Used to determine if product should display quantities in liters instead of kg
   */
  isOilProductWithLiter(product: any): boolean {
    if (!product) return false;

    // Check if default_uom is liter/litr/ltr (API returns "ltr")
    const isLiterUom =
      product.default_uom?.toLowerCase() === 'ltr' ||
      product.default_uom?.toLowerCase() === 'litr' ||
      product.default_uom?.toLowerCase() === 'liter' ||
      product.defaultUom?.toLowerCase() === 'ltr' ||
      product.defaultUom?.toLowerCase() === 'litr' ||
      product.defaultUom?.toLowerCase() === 'liter';

    if (!isLiterUom) return false;

    // Check if category contains oil or fluids (using includes for "OIL / Fluids" format)
    const scrapCategoryLower = product.scrap_category?.toLowerCase() || '';
    const categoryLower = product.category?.toLowerCase() || '';
    const materialLower = product.material?.toLowerCase() || '';
    const materialCompositionLower =
      product.material_composition?.toLowerCase() || '';
    const nameLower = product.name?.toLowerCase() || '';

    const isOilCategory =
      scrapCategoryLower.includes('oil') ||
      scrapCategoryLower.includes('fluids') ||
      categoryLower.includes('oil') ||
      categoryLower.includes('fluids') ||
      materialLower.includes('oil') ||
      materialLower.includes('fluids') ||
      materialCompositionLower.includes('oil') ||
      materialCompositionLower.includes('fluids') ||
      nameLower.includes('oil') ||
      nameLower.includes('fluids');

    const result = isOilCategory && isLiterUom;

    // Debug log for RecentlyViewedParts
    console.log('🔍 RecentlyViewedParts isOilProductWithLiter called:', {
      name: product.table_name || product.name,
      default_uom: product.default_uom,
      material: product.material,
      scrap_category: product.scrap_category,
      isLiterUom: isLiterUom,
      isOilCategory: isOilCategory,
      result: result,
    });

    return result;
  }

  /**
   * Converts fix_quantity to tons
   * Assumes fix_quantity is in kg
   */
  getStockInTons(product: any): number {
    if (!product || !product.fix_quantity) return 0;

    // Convert kg to tons (1 ton = 1000 kg)
    return Math.floor(product.fix_quantity / 1000);
  }

  /**
   * Generates ton dropdown options based on available stock
   */
  getTonOptions(product: any): number[] {
    const stockInTons = this.getStockInTons(product);
    const options: number[] = [];

    // Generate options from 1 to available stock in tons
    // Maximum 10 options to keep dropdown manageable
    const maxOptions = Math.min(stockInTons, 10);

    for (let i = 1; i <= maxOptions; i++) {
      options.push(i);
    }

    return options;
  }

  /**
   * Checks if the product should be disabled due to quantity validation or locked status
   */
  shouldDisableButtons(product: any): boolean {
    if (!product) return true;
    if (product.part_type === 'Scrap Part') {
      const touched = !!product.quantityTouched;
      return (
        product?.is_locked || !touched || !this.isQuantityAvailable(product)
      );
    }
    // For Used Parts, validate against fix_quantity only if it's available
    const selectedQty = this.getSelectedQuantity(product);
    const availableQty = this.getAvailableQuantity(product);
    // If fix_quantity is not set or 0, don't disable (allow add to cart/buy now)
    if (availableQty === 0) {
      return product?.is_locked || false;
    }
    return product?.is_locked || selectedQty > availableQty;
  }

  getCurrentImage(product: any) {
    if (product?.part_image_path && product?.part_image_path.length > 0) {
      return product?.part_image_path[product?.currentImageIndex];
    }
    return 'assets/placeholder.png';
  }

  nextImage(product: any) {
    if (product?.part_image_path && product?.part_image_path.length > 0) {
      product.currentImageIndex =
        (product.currentImageIndex + 1) % product?.part_image_path.length;
    }
  }

  prevImage(product: any) {
    if (product.part_image_path && product?.part_image_path.length > 0) {
      product.currentImageIndex =
        (product.currentImageIndex - 1 + product?.part_image_path.length) %
        product?.part_image_path.length;
    }
  }

  /**
   * Start auto-scrolling images when user hovers over a product
   */
  onProductHover(product: any) {
    // Only work in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.hoveredProduct = product;

    // Clear any existing interval first to prevent multiple intervals
    if (this.imageScrollInterval) {
      clearInterval(this.imageScrollInterval);
      this.imageScrollInterval = null;
    }

    // Only start auto-scroll if product has multiple images
    if (product?.part_image_path && product?.part_image_path.length > 1) {
      // Ensure currentImageIndex is initialized
      if (typeof product.currentImageIndex !== 'number') {
        product.currentImageIndex = 0;
      }

      // Start auto-scrolling every 2 seconds
      this.imageScrollInterval = setInterval(() => {
        if (
          this.hoveredProduct === product &&
          product?.part_image_path?.length > 1
        ) {
          // Calculate next index
          product.currentImageIndex =
            (product.currentImageIndex + 1) % product?.part_image_path.length;
          // Manually trigger change detection to update the view
          this.cdr.detectChanges();
        } else {
          // Clear interval if conditions no longer met
          if (this.imageScrollInterval) {
            clearInterval(this.imageScrollInterval);
            this.imageScrollInterval = null;
          }
        }
      }, 2000);
    }
  }

  /**
   * Stop auto-scrolling images when user stops hovering
   */
  onProductHoverEnd() {
    this.hoveredProduct = null;

    // Clear the interval
    if (this.imageScrollInterval) {
      clearInterval(this.imageScrollInterval);
      this.imageScrollInterval = null;
    }
  }

  gotoProductList() {
    localStorage.removeItem('filterState');
    localStorage.removeItem('category');
    localStorage.removeItem('mostSearchedModel');
    localStorage.removeItem('seemore');
    localStorage.removeItem('categoryType');
    localStorage.removeItem('MyGarageData');
    localStorage.removeItem('SpareCategory');
    localStorage.removeItem('guideSearchData');
    this.router.navigate(['/product-list']);
  }

  ngOnDestroy() {
    // Clear image scroll interval to prevent memory leaks
    if (this.imageScrollInterval) {
      clearInterval(this.imageScrollInterval);
      this.imageScrollInterval = null;
    }

    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}

interface Product {
  id: string;
  imageUrl: string;
  grade: string;
  gradeIconUrl: string;
  title: string;
  manufacturer: string;
  model: string;
  year: string;
  engineSpecs: string;
  currentPrice: string;
  originalPrice: string;
  discountPercentage: string;
  deliveryDate: string;
  storePickupReady: boolean;
  deliveryIconUrl: string;
  storePickupIconUrl: string;
  buyNowIconUrl: string;
  addToCartIconUrl: string;
}
